import {
  PLATFORM_LABELS,
  SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY,
} from "shared/constants/social";
import { isSameDayUTC } from "shared/helpers/date";
import type OrganisationSocialIntegration from "shared/resources/OrganisationSocialIntegration";

export const isSocialPlatformScheduleValid = ({
  socialPlatform,
  scheduledAt,
  tokenRefreshedAt: tokenRefreshedAtMs,
}: Pick<
  OrganisationSocialIntegration,
  "socialPlatform" | "scheduledAt" | "tokenRefreshedAt"
>) => {
  const platform = PLATFORM_LABELS[socialPlatform];
  if (!platform || !scheduledAt) return false;

  const expiryDuration = SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY[platform];

  if (expiryDuration === Infinity) return true;

  const postScheduleDate = Date.parse(scheduledAt);
  const tokenRefreshedAt = Date.parse(tokenRefreshedAtMs);
  const tokenExpiresAt = tokenRefreshedAt + expiryDuration;

  if (isSameDayUTC(postScheduleDate, tokenExpiresAt)) {
    return false;
  }

  return postScheduleDate <= tokenExpiresAt;
};

export const isSocialPlatformTokenValid = ({
  socialPlatform,
  expiresAt,
  tokenRefreshedAt: tokenRefreshedAtMs,
}: Pick<
  OrganisationSocialIntegration,
  "socialPlatform" | "expiresAt" | "tokenRefreshedAt"
>) => {
  const platform = PLATFORM_LABELS[socialPlatform];

  if (!tokenRefreshedAtMs || !platform) return false;

  // In the backend, we update this field with the current dateTime when an integration has expired.
  // And we set this field to null when the token is refreshed.
  if (expiresAt) return false;

  const tokenRefreshedAt = Date.parse(tokenRefreshedAtMs ?? "0");

  const expiryDuration = SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY[platform];
  const now = Date.now();

  const tokenAge = now - tokenRefreshedAt;

  return tokenAge <= expiryDuration;
};

export const willSocialPlatformTokenExpireOnScheduledDay = ({
  socialPlatform,
  scheduledAt,
  tokenRefreshedAt,
}: Pick<
  OrganisationSocialIntegration,
  "socialPlatform" | "scheduledAt" | "tokenRefreshedAt"
>): boolean => {
  const platform = PLATFORM_LABELS[socialPlatform];
  if (!scheduledAt || !tokenRefreshedAt) return false;

  if (!platform) return false;

  const expiryDuration = SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY[platform];

  if (expiryDuration === Infinity) return false; // Never expires

  const scheduledTime = Date.parse(scheduledAt);
  const tokenRefreshedTime = Date.parse(tokenRefreshedAt);
  const tokenExpiresAt = tokenRefreshedTime + expiryDuration;

  return isSameDayUTC(scheduledTime, tokenExpiresAt);
};

export const socialPlatformTokenProjectedExpiry = ({
  socialPlatform,
  expiresAt,
  tokenRefreshedAt,
}: Pick<
  OrganisationSocialIntegration,
  "socialPlatform" | "tokenRefreshedAt" | "expiresAt"
>) => {
  const platform = PLATFORM_LABELS[socialPlatform];
  const expiryDuration = SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY[platform];

  if (expiresAt) return new Date(expiresAt);

  if (expiryDuration === Infinity) return Infinity;

  return new Date(Date.parse(tokenRefreshedAt) + expiryDuration);
};
